import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../api/axiosInstance';
import { PermissionManager } from '../../utils/PermissionManager';

export interface EstimateType {
  estimate_id?: string;
  estimateNumber: string;
  customerName: string;
  phone: string;
  address: string;
  detailedAddress?: string;
  estimateDate: string;
  estimator: string;
  estimatorPhone?: string;
  timestamp?: string;
  isContracted?: boolean;
}

interface CacheEntry {
  data: EstimateType[];
  totalPages: number;
  totalElements: number;
  contractedIds: Set<string>;
  timestamp: number;
}

export interface UseEstimateListReturn {
  // 모달 제어
  showLoadModal: boolean;
  openLoadModal: () => void;
  closeLoadModal: () => void;

  // 목록 데이터
  paginatedEstimates: EstimateType[];
  totalEstimatePages: number;
  loadingEstimates: boolean;
  isFetchingInBackground: boolean;
  totalEstimateElements: number;
  serverTotalElements: number;

  // 페이징/검색/필터
  currentEstimatePage: number;
  handleEstimatePageChange: (page: number) => void;
  searchInput: string;
  handleSearchChange: (value: string) => void;
  filterStartDate: string;
  setFilterStartDate: (d: string) => void;
  filterEndDate: string;
  setFilterEndDate: (d: string) => void;
  filterContractStatus: string;
  setFilterContractStatus: (s: string) => void;
  filterEstimator: string;
  setFilterEstimator: (e: string) => void;
  availableEstimators: string[];
  isAdmin: boolean;

  // 선택/계약
  selectedEstimates: Set<string>;
  setSelectedEstimates: React.Dispatch<React.SetStateAction<Set<string>>>;
  contractedEstimates: Set<string>;
  handleContractCheckbox: (id: string) => void;
  showContractModal: boolean;
  showUncontractModal: boolean;
  confirmContract: () => void;
  confirmUncontract: () => void;
  cancelContract: () => void;
  cancelUncontract: () => void;

  // 공유
  sharedEstimateIds: Set<string>;
  receivedEstimateIds: Set<string>;

  // 미리보기
  showEstimatePreviewModal: boolean;
  setShowEstimatePreviewModal: React.Dispatch<React.SetStateAction<boolean>>;
  previewEstimateNumber: string;
  estimatePreviewLoading: boolean;
  estimatePreviewData: any;
  setEstimatePreviewData: React.Dispatch<React.SetStateAction<any>>;
  previewEstimate: (estimateNumber: string) => void;

  // 불러오기 관련
  loadedEstimateNumber: string | null;
  setLoadedEstimateNumber: React.Dispatch<React.SetStateAction<string | null>>;
  loadEstimatesPage: (pageNum?: number, searchText?: string, startDate?: string, endDate?: string, contractStatus?: string) => Promise<void>;

  // 공유 모달
  showShareModal: boolean;
  setShowShareModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedDepartment: string;
  setSelectedDepartment: React.Dispatch<React.SetStateAction<string>>;
  selectedUserName: string;
  setSelectedUserName: React.Dispatch<React.SetStateAction<string>>;
  departmentUsers: { id: string; name: string }[];
  setDepartmentUsers: React.Dispatch<React.SetStateAction<{ id: string; name: string }[]>>;
  currentUserId: number | null;
}

export function useEstimateList(): UseEstimateListReturn {
  // 모달/목록 상태
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [availableEstimates, setAvailableEstimates] = useState<EstimateType[]>([]);
  const [totalEstimateElements, setTotalEstimateElements] = useState(0);
  const [loadingEstimates, setLoadingEstimates] = useState(false);
  const [loadedEstimateNumber, setLoadedEstimateNumber] = useState<string | null>(null);

  // 공유 모달 상태
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedUserName, setSelectedUserName] = useState('');
  const [departmentUsers, setDepartmentUsers] = useState<{ id: string; name: string }[]>([]);

  // 미리보기 상태
  const [showEstimatePreviewModal, setShowEstimatePreviewModal] = useState(false);
  const [previewEstimateNumber, setPreviewEstimateNumber] = useState<string>('');
  const [estimatePreviewLoading, setEstimatePreviewLoading] = useState(false);
  const [estimatePreviewData, setEstimatePreviewData] = useState<any>(null);

  // 검색/페이징 상태
  const [searchInput, setSearchInput] = useState('');
  const [currentEstimatePage, setCurrentEstimatePage] = useState(1);
  const [estimatesPerPage] = useState(10);
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [selectedEstimates, setSelectedEstimates] = useState<Set<string>>(new Set());
  const [filterEstimator, setFilterEstimator] = useState('');
  const [availableEstimators, setAvailableEstimators] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [filterContractStatus, setFilterContractStatus] = useState('all');
  const [serverTotalPages, setServerTotalPages] = useState(0);
  const [serverTotalElements, setServerTotalElements] = useState(0);

  // 캐시 시스템
  const CACHE_TTL = 5 * 60 * 1000;
  const pageCache = useRef<Map<string, CacheEntry>>(new Map());
  const [isFetchingInBackground, setIsFetchingInBackground] = useState(false);

  // 계약 상태
  const [contractedEstimates, setContractedEstimates] = useState<Set<string>>(new Set());
  const [showContractModal, setShowContractModal] = useState(false);
  const [pendingContractEstimateId, setPendingContractEstimateId] = useState<string>('');
  const [showUncontractModal, setShowUncontractModal] = useState(false);
  const [pendingUncontractEstimateId, setPendingUncontractEstimateId] = useState<string>('');

  // 공유 정보 상태
  const [sharedEstimateIds, setSharedEstimateIds] = useState<Set<string>>(new Set());
  const [receivedEstimateIds, setReceivedEstimateIds] = useState<Set<string>>(new Set());
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);

  // 클라이언트 사이드 필터링
  const [allEstimatesData, setAllEstimatesData] = useState<EstimateType[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // --- 캐시 유틸리티 ---

  const getCacheKey = (page: number, search: string, startDate: string, endDate: string, status: string) => {
    return `${page}-${search}-${startDate}-${endDate}-${status}`;
  };

  const isCacheValid = (entry: CacheEntry | undefined): boolean => {
    if (!entry) return false;
    return Date.now() - entry.timestamp < CACHE_TTL;
  };

  const cleanupCache = () => {
    const now = Date.now();
    pageCache.current.forEach((entry, key) => {
      if (now - entry.timestamp > CACHE_TTL) {
        pageCache.current.delete(key);
      }
    });
  };

  const clearEstimateCache = () => {
    pageCache.current.clear();
  };

  // --- 공유 정보 로드 ---

  const loadShareInfo = async (estimatesData?: any[]) => {
    try {
      const userResponse = await api.get('/api/UserName');
      if (userResponse.data) {
        const currentUserIdValue = userResponse.data.id;
        const roleId = userResponse.data.roleId;
        setCurrentUserId(currentUserIdValue);

        if (roleId) {
          localStorage.setItem('roleId', String(roleId));
        }

        const [sharedToMeResponse, sharedByMeResponse] = await Promise.allSettled([
          api.get('/api/estimates/shared-to-me'),
          api.get('/api/estimates/shared-by-me'),
        ]);

        if (sharedToMeResponse.status === 'fulfilled') {
          const sharedToMeEstimateIds = sharedToMeResponse.value.data || [];
          setReceivedEstimateIds(new Set(sharedToMeEstimateIds.map((id: number) => id.toString())));
        } else {
          console.error('공유받은 견적 조회 실패:', sharedToMeResponse.reason);
          setReceivedEstimateIds(new Set());
        }

        if (sharedByMeResponse.status === 'fulfilled') {
          const sharedByMeEstimateIds = sharedByMeResponse.value.data || [];
          setSharedEstimateIds(new Set(sharedByMeEstimateIds.map((id: number) => id.toString())));
        } else {
          console.error('공유한 견적 조회 실패:', sharedByMeResponse.reason);
          setSharedEstimateIds(new Set());
        }
      }
    } catch (error) {
      console.error('공유 정보 로드 실패:', error);
    }
  };

  // --- 서버 데이터 fetch ---

  const fetchEstimatesFromServer = async (
    page: number,
    search: string,
    start: string,
    end: string,
    status: string
  ): Promise<CacheEntry | null> => {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('size', String(estimatesPerPage));

    if (search && search.trim()) {
      params.append('search', search.trim());
    }
    if (start) {
      params.append('startDate', start);
    }
    if (end) {
      params.append('endDate', end);
    }
    if (status && status !== 'all') {
      if (status === 'contracted' || status === 'not_contracted') {
        params.append('contractStatus', status);
      }
    }

    const response = await api.get(`/api/estimates/recent?${params.toString()}`);

    if (response.data) {
      const { content, totalPages, totalElements, contractedIds } = response.data;

      let contractedSet: Set<string>;
      if (contractedIds) {
        contractedSet = new Set(contractedIds);
      } else if (content && content.length > 0) {
        const ids = content
          .filter((estimate: EstimateType) => estimate.isContracted === true)
          .map((estimate: EstimateType) => estimate.estimate_id?.toString())
          .filter((id: string | undefined) => id !== undefined);
        contractedSet = new Set(ids as string[]);
      } else {
        contractedSet = new Set();
      }

      return {
        data: content || [],
        totalPages: totalPages || 0,
        totalElements: totalElements || 0,
        contractedIds: contractedSet,
        timestamp: Date.now(),
      };
    }
    return null;
  };

  // --- 프리페칭 ---

  const PREFETCH_RANGE = 2;
  const prefetchAdjacentPages = async (
    currentPage: number,
    totalPages: number,
    search: string,
    start: string,
    end: string,
    status: string
  ) => {
    const pagesToPrefetch: number[] = [];

    for (let i = 1; i <= PREFETCH_RANGE; i++) {
      if (currentPage - i >= 0) {
        pagesToPrefetch.push(currentPage - i);
      }
    }
    for (let i = 1; i <= PREFETCH_RANGE; i++) {
      if (currentPage + i < totalPages) {
        pagesToPrefetch.push(currentPage + i);
      }
    }

    for (const pageToFetch of pagesToPrefetch) {
      const cacheKey = getCacheKey(pageToFetch, search, start, end, status);
      const cachedEntry = pageCache.current.get(cacheKey);

      if (!isCacheValid(cachedEntry)) {
        try {
          const result = await fetchEstimatesFromServer(pageToFetch, search, start, end, status);
          if (result) {
            pageCache.current.set(cacheKey, result);
          }
        } catch (error) {
          // 프리페치 실패는 무시
        }
      }
    }
  };

  // --- 메인 페이지 로드 ---

  const loadEstimatesPage = async (
    pageNum?: number,
    searchText?: string,
    startDate?: string,
    endDate?: string,
    contractStatus?: string
  ) => {
    const page = (pageNum ?? currentEstimatePage) - 1;
    const search = searchText ?? debouncedSearch;
    const start = startDate ?? filterStartDate;
    const end = endDate ?? filterEndDate;
    const status = contractStatus ?? filterContractStatus;

    const cacheKey = getCacheKey(page, search, start, end, status);
    const cachedEntry = pageCache.current.get(cacheKey);

    // 1. 캐시가 유효하면 캐시 데이터 사용
    if (isCacheValid(cachedEntry)) {
      setAllEstimatesData(cachedEntry!.data);
      setAvailableEstimates(cachedEntry!.data);
      setServerTotalPages(cachedEntry!.totalPages);
      setServerTotalElements(cachedEntry!.totalElements);
      setTotalEstimateElements(cachedEntry!.totalElements);
      setContractedEstimates(cachedEntry!.contractedIds);
      setLoadingEstimates(false);

      if (cachedEntry!.data.length > 0) {
        loadShareInfo(cachedEntry!.data);
      }

      prefetchAdjacentPages(page, cachedEntry!.totalPages, search, start, end, status);
      return;
    }

    // 2. SWR 패턴: 만료된 캐시 데이터 먼저 표시
    if (cachedEntry) {
      setAllEstimatesData(cachedEntry.data);
      setAvailableEstimates(cachedEntry.data);
      setServerTotalPages(cachedEntry.totalPages);
      setServerTotalElements(cachedEntry.totalElements);
      setTotalEstimateElements(cachedEntry.totalElements);
      setContractedEstimates(cachedEntry.contractedIds);
      setLoadingEstimates(false);
      setIsFetchingInBackground(true);

      try {
        const newData = await fetchEstimatesFromServer(page, search, start, end, status);
        if (newData) {
          pageCache.current.set(cacheKey, newData);
          setAllEstimatesData(newData.data);
          setAvailableEstimates(newData.data);
          setServerTotalPages(newData.totalPages);
          setServerTotalElements(newData.totalElements);
          setTotalEstimateElements(newData.totalElements);
          setContractedEstimates(newData.contractedIds);

          if (newData.data.length > 0) {
            await loadShareInfo(newData.data);
          }

          prefetchAdjacentPages(page, newData.totalPages, search, start, end, status);
        }
      } catch (error) {
        console.error('백그라운드 데이터 로드 실패:', error);
      } finally {
        setIsFetchingInBackground(false);
      }
      return;
    }

    // 3. 캐시 없으면 서버에서 로드
    setLoadingEstimates(true);

    try {
      cleanupCache();
      const result = await fetchEstimatesFromServer(page, search, start, end, status);

      if (result) {
        pageCache.current.set(cacheKey, result);
        setAllEstimatesData(result.data);
        setAvailableEstimates(result.data);
        setServerTotalPages(result.totalPages);
        setServerTotalElements(result.totalElements);
        setTotalEstimateElements(result.totalElements);
        setContractedEstimates(result.contractedIds);

        if (result.data.length > 0) {
          await loadShareInfo(result.data);
        }

        prefetchAdjacentPages(page, result.totalPages, search, start, end, status);
      } else {
        console.error('견적 목록 조회 실패');
        setAllEstimatesData([]);
        setAvailableEstimates([]);
        setServerTotalPages(0);
        setServerTotalElements(0);
      }
    } catch (error) {
      console.error('견적 목록 조회 중 오류:', error);
      setAllEstimatesData([]);
      setServerTotalPages(0);
      setServerTotalElements(0);
    } finally {
      setLoadingEstimates(false);
    }
  };

  // --- 검색/필터 ---

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    setDebouncedSearch(value);
  }, []);

  const filteredEstimates = useMemo(() => {
    if (!allEstimatesData || allEstimatesData.length === 0) return [];

    let result = allEstimatesData;

    if (filterContractStatus === 'shared') {
      if (PermissionManager.getEstimateLoadScope() === 'ALL') {
        result = result.filter(estimate =>
          sharedEstimateIds.has(estimate.estimate_id?.toString() || '')
        );
      }
    } else if (filterContractStatus === 'received') {
      result = result.filter(estimate =>
        receivedEstimateIds.has(estimate.estimate_id?.toString() || '')
      );
    }

    return result;
  }, [allEstimatesData, filterContractStatus, sharedEstimateIds, receivedEstimateIds]);

  const handleEstimatePageChange = (page: number) => {
    if (page >= 1 && page <= serverTotalPages) {
      setCurrentEstimatePage(page);
      loadEstimatesPage(page, debouncedSearch, filterStartDate, filterEndDate, filterContractStatus);
    }
  };

  const totalEstimatePages = useMemo(() => {
    if (filterContractStatus === 'shared' || filterContractStatus === 'received') {
      return Math.ceil(filteredEstimates.length / estimatesPerPage);
    }
    return serverTotalPages;
  }, [serverTotalPages, filterContractStatus, filteredEstimates.length, estimatesPerPage]);

  const paginatedEstimates = useMemo(() => {
    if (!filteredEstimates || filteredEstimates.length === 0) return [];

    if (filterContractStatus === 'shared' || filterContractStatus === 'received') {
      const start = (currentEstimatePage - 1) * estimatesPerPage;
      const end = Math.min(start + estimatesPerPage, filteredEstimates.length);
      return filteredEstimates.slice(start, end);
    }

    return filteredEstimates;
  }, [filteredEstimates, currentEstimatePage, estimatesPerPage, filterContractStatus]);

  // --- 모달 관리 ---

  const openLoadModal = async () => {
    setSearchInput('');
    setCurrentEstimatePage(1);
    setSelectedEstimates(new Set());
    setIsInitialLoad(true);
    setShowLoadModal(true);
    setLoadingEstimates(true);

    setSearchInput('');
    setDebouncedSearch('');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterContractStatus('all');
    setCurrentEstimatePage(1);

    setTimeout(() => {
      loadEstimatesPage(1, '', '', '', 'all').finally(() => {
        setIsInitialLoad(false);
      });
    }, 10);
  };

  const closeLoadModal = () => {
    setShowLoadModal(false);
    setLoadedEstimateNumber(null);
    setAllEstimatesData([]);
    setAvailableEstimates([]);
    setIsInitialLoad(false);
  };

  // --- 미리보기 ---

  const previewEstimate = async (estimateNumber: string) => {
    setPreviewEstimateNumber(estimateNumber);
    setEstimatePreviewLoading(true);
    setShowEstimatePreviewModal(true);

    try {
      const response = await api.get(`/api/estimates/${estimateNumber}`);
      if (response.data) {
        setEstimatePreviewData(response.data);
      }
    } catch (error) {
      console.error('견적서 미리보기 실패:', error);
      alert('견적서 미리보기를 불러오는데 실패했습니다.');
      setShowEstimatePreviewModal(false);
    } finally {
      setEstimatePreviewLoading(false);
    }
  };

  // --- 계약 관리 ---

  const extractCustomerIdentifier = (estimateNumber: string): string => {
    const parts = estimateNumber.split('-');
    if (parts.length >= 3) {
      return `${parts[1]}-${parts[2]}`;
    }
    return '';
  };

  const updateContractStatus = async (estimateId: string, isContracted: boolean) => {
    try {
      const response = await api.put(`/api/estimates/${estimateId}/contract`, {
        isContracted: isContracted,
      });

      if (response.data) {
        return true;
      }
    } catch (error) {
      console.error('계약 상태 업데이트 실패:', error);
      alert('계약 상태 업데이트에 실패했습니다.');
      return false;
    }
    return false;
  };

  const handleContractCheckbox = async (estimateId: string) => {
    if (contractedEstimates.has(estimateId)) {
      setPendingUncontractEstimateId(estimateId);
      setShowUncontractModal(true);
    } else {
      const targetEstimate = allEstimatesData.find(est => est.estimate_id?.toString() === estimateId);
      if (!targetEstimate) return;

      const targetCustomerIdentifier = extractCustomerIdentifier(targetEstimate.estimateNumber);

      const isCustomerAlreadyContracted = Array.from(contractedEstimates).some(contractedId => {
        const contractedEstimate = allEstimatesData.find(est => est.estimate_id?.toString() === contractedId);
        if (!contractedEstimate) return false;

        const contractedCustomerIdentifier = extractCustomerIdentifier(contractedEstimate.estimateNumber);
        return contractedCustomerIdentifier === targetCustomerIdentifier;
      });

      if (isCustomerAlreadyContracted) {
        alert('해당 고객의 다른 견적이 이미 계약되어 있습니다. 고객당 하나의 견적만 계약 가능합니다.');
        return;
      }

      setPendingContractEstimateId(estimateId);
      setShowContractModal(true);
    }
  };

  const confirmContract = async () => {
    if (pendingContractEstimateId) {
      const success = await updateContractStatus(pendingContractEstimateId, true);
      if (success) {
        const newContracted = new Set(contractedEstimates);
        newContracted.add(pendingContractEstimateId);
        setContractedEstimates(newContracted);

        setAllEstimatesData(prev => prev.map(est =>
          est.estimate_id?.toString() === pendingContractEstimateId
            ? { ...est, isContracted: true }
            : est
        ));

        // 캐시 무효화 — 모달 재진입 시 서버에서 최신 데이터 로드
        pageCache.current.clear();
      }
    }
    setShowContractModal(false);
    setPendingContractEstimateId('');
  };

  const cancelContract = () => {
    setShowContractModal(false);
    setPendingContractEstimateId('');
  };

  const confirmUncontract = async () => {
    if (pendingUncontractEstimateId) {
      const success = await updateContractStatus(pendingUncontractEstimateId, false);
      if (success) {
        const newContracted = new Set(contractedEstimates);
        newContracted.delete(pendingUncontractEstimateId);
        setContractedEstimates(newContracted);

        setAllEstimatesData(prev => prev.map(est =>
          est.estimate_id?.toString() === pendingUncontractEstimateId
            ? { ...est, isContracted: false }
            : est
        ));

        // 캐시 무효화 — 모달 재진입 시 서버에서 최신 데이터 로드
        pageCache.current.clear();
      }
    }
    setShowUncontractModal(false);
    setPendingUncontractEstimateId('');
  };

  const cancelUncontract = () => {
    setShowUncontractModal(false);
    setPendingUncontractEstimateId('');
  };

  // --- useEffects ---

  // 관리자 여부 확인 및 견적자 목록 불러오기 (모달 열 때)
  useEffect(() => {
    if (showLoadModal) {
      const loadEstimatesAndInfo = async () => {
        await loadEstimatesPage();
      };
      loadEstimatesAndInfo();

      const checkAdminAndLoadEstimators = async () => {
        try {
          const userResponse = await api.get('/api/UserName');
          if (userResponse.data) {
            const userData = userResponse.data;
            const currentUser = userData.name;
            const isCurrentUserAdmin = currentUser === '관리자';
            setIsAdmin(isCurrentUserAdmin);

            if (isCurrentUserAdmin) {
              try {
                const estimatorsResponse = await api.get('/api/estimates/estimators');
                if (estimatorsResponse.data && estimatorsResponse.data) {
                  const estimators = estimatorsResponse.data;
                  setAvailableEstimators(estimators);
                } else {
                  setAvailableEstimators([]);
                }
              } catch (error) {
                console.error('견적자 목록 조회 실패:', error);
                setAvailableEstimators([]);
              }
            } else {
              setAvailableEstimators([currentUser]);
            }
          }
        } catch (error) {
          console.error('사용자 정보 조회 실패:', error);
          setAvailableEstimators([]);
        }
      };

      checkAdminAndLoadEstimators();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLoadModal]);

  // 필터링 조건 변경 시
  useEffect(() => {
    if (showLoadModal && !isInitialLoad) {
      clearEstimateCache();
      setCurrentEstimatePage(1);
      loadEstimatesPage(1, debouncedSearch, filterStartDate, filterEndDate, filterContractStatus);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStartDate, filterEndDate, debouncedSearch, filterContractStatus]);

  return {
    // 모달 제어
    showLoadModal,
    openLoadModal,
    closeLoadModal,

    // 목록 데이터
    paginatedEstimates,
    totalEstimatePages,
    loadingEstimates,
    isFetchingInBackground,
    totalEstimateElements,
    serverTotalElements,

    // 페이징/검색/필터
    currentEstimatePage,
    handleEstimatePageChange,
    searchInput,
    handleSearchChange,
    filterStartDate,
    setFilterStartDate,
    filterEndDate,
    setFilterEndDate,
    filterContractStatus,
    setFilterContractStatus,
    filterEstimator,
    setFilterEstimator,
    availableEstimators,
    isAdmin,

    // 선택/계약
    selectedEstimates,
    setSelectedEstimates,
    contractedEstimates,
    handleContractCheckbox,
    showContractModal,
    showUncontractModal,
    confirmContract,
    confirmUncontract,
    cancelContract,
    cancelUncontract,

    // 공유
    sharedEstimateIds,
    receivedEstimateIds,

    // 미리보기
    showEstimatePreviewModal,
    setShowEstimatePreviewModal,
    previewEstimateNumber,
    estimatePreviewLoading,
    estimatePreviewData,
    setEstimatePreviewData,
    previewEstimate,

    // 불러오기 관련
    loadedEstimateNumber,
    setLoadedEstimateNumber,
    loadEstimatesPage,

    // 공유 모달
    showShareModal,
    setShowShareModal,
    selectedDepartment,
    setSelectedDepartment,
    selectedUserName,
    setSelectedUserName,
    departmentUsers,
    setDepartmentUsers,
    currentUserId,
  };
}
